import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isSectionHeaderLayout } from "@/office/powerpoint/design";
import { readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_ABSCHNITTE } from "../makros";

export const lektionAbschnitte: Lesson = {
  id: "abschnitte",
  title: "Abschnitte",
  summary: "Kapitel für die Miniaturleiste – und Trennfolien für das Publikum.",
  durationMinutes: 9,
  learningGoals: [
    "Abschnitte in der Miniaturleiste anlegen und benennen",
    "Abschnittstrenner als eigene Folien einsetzen",
    "Große Präsentationen über Abschnitte umbauen",
  ],
  steps: [
    {
      id: "abschnitte-anlegen",
      title: "Abschnitte in der Miniaturleiste",
      explanation: [
        "Ab etwa zwanzig Folien wird das Scrollen in der Miniaturleiste mühsam. " +
          "Abschnitte lösen das: Rechtsklick in die Miniaturleiste zwischen zwei Folien " +
          "→ „Abschnitt hinzufügen“, dann einen Namen vergeben. Ab dieser Stelle beginnt " +
          "ein neuer Abschnitt.",
        "Jeder Abschnitt lässt sich über das Dreieck vor dem Namen zuklappen. Aus " +
          "vierzig Folien werden so fünf Zeilen, und die Präsentation wird auf einmal " +
          "wieder überschaubar.",
        "Der praktische Hauptnutzen zeigt sich beim Umbauen: Ein zugeklappter Abschnitt " +
          "lässt sich als Ganzes verschieben. Ein komplettes Kapitel nach vorn zu ziehen " +
          "ist damit eine Bewegung statt fünfzehn.",
        "Abschnitte sind außerdem die Grundlage für den Abschnittszoom und für die " +
          "Auswahl beim Drucken. Und sie stehen in der Referentenansicht: Beim Springen " +
          "zu einer Folie sind die Abschnittsnamen dort mit aufgeführt.",
      ],
      keyPoints: [
        "Rechtsklick in die Miniaturleiste → Abschnitt hinzufügen.",
        "Das Dreieck vor dem Namen klappt einen Abschnitt zu.",
        "Zugeklappte Abschnitte lassen sich als Ganzes verschieben.",
      ],
      task: {
        instruction:
          "Teile deine Präsentation in mindestens zwei Abschnitte auf und gib jedem einen " +
          "sprechenden Namen.",
        hint: "Rechtsklick in die Miniaturleiste vor die erste Folie eines Kapitels → Abschnitt hinzufügen → Namen eingeben.",
        macro: MAKRO_ABSCHNITTE,
        check: () =>
          pruefeMitMakro(MAKRO_ABSCHNITTE, (bericht) => {
            const abschnitte = [...bericht.gruppe("abschnitt").values()];
            const unbenannt = abschnitte.filter((abschnitt) =>
              /^(Standardabschnitt|Default Section|Unbenannter Abschnitt|Untitled Section)$/i.test(abschnitt.get("name") ?? ""),
            );
            return fromCriteria(
              [
                criterion(
                  `Mindestens zwei Abschnitte (gefunden: ${abschnitte.map((a) => a.get("name")).join(", ") || "keine"})`,
                  abschnitte.length >= 2,
                ),
                criterion(`Kein Abschnitt trägt noch den Standardnamen (gefunden: ${unbenannt.length})`, unbenannt.length === 0),
              ],
              {
                success: "Die Präsentation ist in Kapitel gegliedert – zuklappbar und als Ganzes verschiebbar.",
                failure: "Die Abschnitte fehlen noch oder tragen noch Standardnamen.",
                hint: "Rechtsklick auf den Abschnittsnamen → Abschnitt umbenennen.",
              },
            );
          }),
      },
    },
    {
      id: "trennfolie",
      title: "Abschnittstrenner für das Publikum",
      explanation: [
        "Abschnitte in der Miniaturleiste sieht nur, wer die Datei bearbeitet. Für das " +
          "Publikum braucht es etwas Sichtbares: eine Trennfolie mit dem Layout " +
          "„Abschnittsüberschrift“.",
        "Diese Folien sind mehr als Dekoration. Sie geben dem Publikum einen Moment zum " +
          "Sortieren – „das Thema ist abgeschlossen, jetzt kommt etwas Neues“ –, und der " +
          "vortragenden Person einen natürlichen Punkt zum Luftholen und für " +
          "Zwischenfragen.",
        "Das Layout „Abschnittsüberschrift“ bringt dafür genau das Richtige mit: einen " +
          "großen Titel und darunter eine kurze Beschreibung, was in diesem Abschnitt " +
          "kommt. Beides sind Platzhalter – die Trennfolie taucht damit sauber in der " +
          "Gliederungsansicht auf und wird von Screenreadern vorgelesen.",
        "Als Größenordnung: Bei mehr als etwa fünfzehn Folien lohnen sich Abschnitte, " +
          "und ein Abschnitt sollte nicht länger als sieben bis zehn Folien sein.",
      ],
      keyPoints: [
        "Start → Layout → „Abschnittsüberschrift“ für die Trennfolie.",
        "Trennfolien geben dem Publikum eine Pause zum Sortieren.",
        "Richtwert: ein Abschnitt umfasst sieben bis zehn Folien.",
      ],
      task: {
        instruction:
          "Lege in deiner Präsentation mindestens eine Trennfolie mit dem Layout " +
          "„Abschnittsüberschrift“ an und gib ihr einen Titel.",
        hint: "Neue Folie einfügen, dann Start → Layout → „Abschnittsüberschrift“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Das Layout einer Folie",
              "1.3",
              "Prüfe unter „Start“ → „Layout“, ob eine deiner Folien auf „Abschnittsüberschrift“ steht.",
            );
          }

          const trenner = snapshot.slides.filter((folie) =>
            isSectionHeaderLayout(folie.layoutType, folie.layoutName),
          );

          if (trenner.length === 0) {
            const nurNamen = !snapshot.features.layoutType;
            return fail(
              "Keine Folie verwendet das Layout „Abschnittsüberschrift“.",
              nurNamen
                ? "Diese PowerPoint-Version liefert nur den Layoutnamen, nicht die sprachunabhängige Layoutart. " +
                  "Gesucht wurde deshalb nach einem Namen, der „Abschnitt“ enthält. Weise einer Folie über „Start“ → „Layout“ die Abschnittsüberschrift zu."
                : "Neue Folie einfügen, dann Start → Layout → „Abschnittsüberschrift“ wählen.",
            );
          }

          const ohneTitel = trenner.filter((folie) =>
            folie.shapes.every((form) => form.paragraphs.length === 0),
          );
          if (ohneTitel.length > 0) {
            return fail(
              `Folie ${ohneTitel[0].index + 1} nutzt das Trennerlayout, ist aber noch leer.`,
              "Schreib den Namen des Abschnitts in den Titelplatzhalter – dann steht er auch in der Gliederung.",
            );
          }

          return pass(
            `${trenner.length === 1 ? "Eine Trennfolie" : `${trenner.length} Trennfolien`} gefunden, ` +
              `zum Beispiel Folie ${trenner[0].index + 1}.`,
          );
        },
      },
    },
    {
      id: "abschnitte-und-trennfolien",
      title: "Abschnitt und Trennfolie gehören zusammen",
      explanation: [
        "Abschnitte in der Miniaturleiste und Trennfolien sind zwei Seiten derselben " +
          "Gliederung: Der Abschnitt ordnet die Datei für die Bearbeitung, die Trennfolie " +
          "ordnet den Vortrag für das Publikum. Am besten beginnt jeder Abschnitt mit seiner " +
          "Trennfolie, und beide tragen denselben Namen.",
        "Office.js kennt Abschnitte nicht – deshalb prüft der Kurs sie über ein " +
          "Prüfmakro, das die Abschnitte über das VBA-Objektmodell ausliest. Die Trennfolien " +
          "dagegen sind ganz normale Folien mit einem Layout und lassen sich direkt prüfen.",
        "Rechtsklick auf einen Abschnittsnamen → „Alle reduzieren“ klappt alle Abschnitte " +
          "auf einmal zu. Dann zeigt die Miniaturleiste nur noch das Inhaltsverzeichnis der " +
          "Präsentation.",
      ],
      keyPoints: [
        "Jeder Abschnitt beginnt mit seiner Trennfolie.",
        "Abschnitt und Trennfolie tragen denselben Namen.",
        "Rechtsklick → „Alle reduzieren“ zeigt die Gliederung.",
      ],
    },
  ],
};
